import css from './alarmSituationInputFormula.module.css'
import { useEffect, useMemo, useRef, useState } from 'react'
import DropdownFadeInPanel from '@/shared/Dropdowns/DropdownFadeInPanel'
import DropdownListItem from '@/shared/Dropdowns/DropdownListItem'
import useParameters from '@/pages/Parameters/hooks/useParameters.ts'
import { IAlarmSituation } from '../../alarmSituations.types.ts'
import alarmSituationInputFormulaConstants from './alarmSituationInputFormula.constants.ts'
import alarmSituationUtils from '../../alarmSituations.utils.ts'
import CopyButton from '@/shared/Buttons/CopyButton'
import domUtils from '@/utils/dom.utils.ts'
import alarmSituationFormulaInputUtils from '@/pages/Processes/AlarmSituations/components/AlarmSituationInputFormula/alarmSituationFormulaInput.utils.ts'
import EmptyPlaceholder from '@/shared/Common/EmptyPlaceholder'
import useSkipFirstDebounce from '@/shared/hooks/useSkipFirstDebounce.ts'

interface IProps {
  value: IAlarmSituation['formula']
  onChange?: (formula: IAlarmSituation['formula'], name: string) => void
  name?: string
  disabled?: boolean
}

const AlarmSituationInputFormula = ({ name, value, onChange, disabled }: IProps) => {
  const [text, setText] = useState('')
  const [isActive, setIsActive] = useState(false)
  const [isOpenDropdown, setIsOpenDropdown] = useState(false)
  const inputRef = useRef<HTMLDivElement>(null)
  const parameters = useParameters()

  const currentInput = useMemo(() => {
    const tokens = alarmSituationUtils.splitFormulaTokens(text)
    return tokens[tokens.length - 1] || ''
  }, [text])

  const debouncedCurrentInput = useSkipFirstDebounce(currentInput, 350)

  const filterParameters = useMemo(() => {
    if (!currentInput) return []

    return parameters.filter((parameter) => parameter.sourceId.toLowerCase().startsWith(currentInput.toLowerCase()))
  }, [debouncedCurrentInput, parameters])

  useEffect(() => {
    if (value) {
      setText(value.map((token) => token.value).join(''))
    }
  }, [value])

  useEffect(() => {
    if (inputRef.current && text) {
      const caretPosition = alarmSituationUtils.saveCaretPosition(inputRef.current)
      alarmSituationFormulaInputUtils.formatFormulaTextAndInsertIntoElement(text, inputRef.current)
      if (caretPosition !== null) {
        alarmSituationUtils.restoreCaretPosition(inputRef.current, caretPosition)
      }
    }
  }, [text])

  useEffect(() => {
    setIsOpenDropdown(text !== '' && isNaN(+currentInput) && /[a-zA-Zа-яА-Я0-9]$/.test(currentInput))
  }, [currentInput])

  const handleSelectParameter = (parameter: { sourceId: string }) => {
    if (value[value.length - 1].type === 'parameter') {
      const newValue = [...value]
      newValue.pop()

      onChange([...newValue, { type: 'parameter', value: parameter.sourceId }], name)
    } else {
      onChange([...value, { type: 'parameter', value: parameter.sourceId }], name)
    }

    setText((prev) => prev.slice(0, prev.lastIndexOf(currentInput)) + parameter.sourceId)
    setIsOpenDropdown(false)

    inputRef.current.focus()
    requestAnimationFrame(() => {
      domUtils.placeCaretAtEnd(inputRef.current)
    })
  }

  const handleDropdownBlur = () => {
    setIsOpenDropdown(false)
  }

  const handleInput = (ev: React.FormEvent<HTMLDivElement>) => {
    const value = Array.from(ev.currentTarget.childNodes)
      .map((node) => (node.textContent ? node.textContent : ''))
      .join('')
    const newValue = value.replace(alarmSituationInputFormulaConstants.invalidCharactersRegex, '')

    const isValid = alarmSituationUtils.isValidSequence(newValue)

    if (!isValid) {
      ev.preventDefault()
      return
    }

    setText(newValue)
    onChange(alarmSituationFormulaInputUtils.parseFormulaTextIntoTokens(newValue), name)

    // В случае, если был отрезан запрещенный символ,
    // text не изменился, а заначит, useEffect не сработает, форсированный перерендер для правильной постановки курсора
    if (newValue !== value) {
      alarmSituationFormulaInputUtils.formatFormulaTextAndInsertIntoElement(text, inputRef.current)
      requestAnimationFrame(() => {
        domUtils.placeCaretAtEnd(inputRef.current)
      })
    }
  }

  return (
    <div style={{ position: 'relative' }} onFocus={() => setIsActive(true)} onBlur={handleDropdownBlur}>
      <div
        contentEditable={!disabled}
        ref={inputRef}
        className={css.customInput}
        data-active={isActive}
        data-disabled={disabled}
        onInput={handleInput}
        onFocus={() => setIsActive(true)}
        onBlur={() => setIsActive(false)}
        suppressContentEditableWarning
        spellCheck="false"
      />
      <div className={css.copyButton} data-disabled={disabled}>
        <CopyButton text={text} />
      </div>
      <DropdownFadeInPanel open={isOpenDropdown && !disabled}>
        {filterParameters.map((parameter) => (
          <DropdownListItem key={parameter.sourceId} active={false} onClick={() => handleSelectParameter(parameter)}>
            <div className="flex-space-between">
              <p className="text-nm">{parameter.sourceId}</p>
            </div>
          </DropdownListItem>
        ))}
        {!filterParameters.length && isOpenDropdown && (
          <EmptyPlaceholder inscription="Параметров не найдено" style={{ padding: '20px' }} />
        )}
      </DropdownFadeInPanel>
    </div>
  )
}

export default AlarmSituationInputFormula
